import { JSX } from "preact"
import { useState, useMemo, useRef, useEffect } from "preact/hooks"
import { CalendarInfo, Promotion } from '../../promo_creation/types'
import { PromotionBlock } from './promotion_block'
import { CalendarFilters } from './calendar_filters'
import { EnhancedPromotionEditModal } from './enhanced_promotion_edit_modal'
import { Button } from '../../../../components/ui/button'
import { parseISO, startOfWeek, endOfWeek, addWeeks, isWithinInterval, getISOWeek, getYear } from 'date-fns'
import type { CalendarPromotionLoadState } from "../../promo_creation/hooks/use_promotional_context_api"
import {
  PromotionFilterSelection,
  has_active_chip_filters,
  promotion_filter_options,
  promotion_matches_filters,
} from '../promotion_filters'
import { FilteredSpendSummary } from './filtered_spend_summary'

interface PromotionalCalendarProps {
  calendars: CalendarInfo[]
  promotion_load_by_calendar: Record<string, CalendarPromotionLoadState>
  className?: string
  onEditPromotion?: (promotion: Promotion) => void
  /** Deletes a promotion from the grid's edit modal; the caller refreshes its calendars. */
  onDeletePromotion?: (promotionId: string) => void | Promise<void>
  selectedYear: number
  onYearChange: (year: number) => void
  searchTerm: string
  onSearchChange: (term: string) => void
  /** Chip selections, owned by the parent so the Accrual view filters the same way. */
  filters: PromotionFilterSelection
  onFiltersChange: (filters: PromotionFilterSelection) => void
}

interface PromotionRow {
  customer: string
  retailer: string
  promotions: Promotion[]
}

export function PromotionalCalendar({ 
  calendars, 
  promotion_load_by_calendar,
  className, 
  onEditPromotion, 
  onDeletePromotion,
  selectedYear, 
  onYearChange, 
  searchTerm, 
  onSearchChange,
  filters,
  onFiltersChange,
}: PromotionalCalendarProps) {
  const calendarScrollRef = useRef<HTMLDivElement>(null)

  // Enhanced edit modal state
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  // Whether the timeline is scrolled to its far left/right edge, where a jump to the
  // previous/next year is offered (the year buttons above are easy to miss).
  const [timelineEdges, setTimelineEdges] = useState({ atLeftEdge: false, atRightEdge: false })

  // Where the timeline should scroll after the next year change. Jumping to the previous year
  // from the left edge lands at the *end* of that year, so the view stays continuous with where
  // the user just was instead of teleporting to January.
  const pendingScrollTargetRef = useRef<'year_start' | 'year_end'>('year_start')

  function handleTimelineScroll(event: JSX.TargetedEvent<HTMLDivElement, Event>) {
    const el = event.currentTarget
    setTimelineEdges(previous => {
      const next = timelineEdgeState(el.scrollLeft, el.clientWidth, el.scrollWidth)
      return next.atLeftEdge === previous.atLeftEdge && next.atRightEdge === previous.atRightEdge ? previous : next
    })
  }

  // Generate weeks for the selected year only (plus edge padding for boundary-spanning promos)
  const weeks = useMemo(() => generateWeeksForYear(selectedYear), [selectedYear])

  // Extract all promotions from all calendars
  const allPromotions = useMemo(() => {
    return calendars.flatMap(calendar => calendar.promotions)
  }, [calendars])

  // Extract unique filter options
  const filterOptions = useMemo(() => promotion_filter_options(allPromotions), [allPromotions])

  // Position the scroll when the year changes: at January normally (the grid starts with padding
  // weeks from the prior December, which shouldn't be the first thing in view), or at the end of
  // the year when the user jumped backwards from the left edge.
  useEffect(() => {
    const scroller = calendarScrollRef.current
    if (scroller) {
      if (pendingScrollTargetRef.current === 'year_end') {
        scroller.scrollTo({ left: scroller.scrollWidth - scroller.clientWidth, behavior: 'smooth' })
      } else {
        const jan1 = new Date(selectedYear, 0, 1)
        const yearStartWeek = weeks.findIndex(week => week.startDate <= jan1 && jan1 <= week.endDate)

        if (yearStartWeek !== -1) {
          const scrollPosition = yearStartWeek * 100 // 100px per week
          scroller.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
          })
        }
      }
    }
    pendingScrollTargetRef.current = 'year_start'
    // The jump buttons belong to the previous scroll position; hide them until the user
    // scrolls again (the smooth scroll above fires scroll events that recompute them).
    setTimelineEdges({ atLeftEdge: false, atRightEdge: false })
  }, [selectedYear, weeks])

  // Auto-scroll the timeline to the first matching promotion while searching, so results that
  // sit months away (e.g. found by remote ID like "S000B") don't require manual horizontal scrolling.
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Promotions passing every filter except the year, so search can also report matches that
  // live in other years (the grid itself only renders the selected year).
  const nonYearFilteredPromotions = useMemo(() => {
    const selection = { ...filters, search: searchTerm }
    return allPromotions.filter(promotion => promotion_matches_filters(promotion, selection))
  }, [allPromotions, filters, searchTerm])

  // Filter promotions to those touching the selected year
  const filteredPromotions = useMemo(() => {
    return nonYearFilteredPromotions.filter(promotion => promotionIntersectsYear(promotion, selectedYear))
  }, [nonYearFilteredPromotions, selectedYear])

  // While searching, count matches that fall entirely in other years so the user can jump there.
  const otherYearSearchMatches = useMemo(() => {
    if (!searchTerm) return []
    const counts = new Map<number, number>()
    for (const promotion of nonYearFilteredPromotions) {
      if (promotionIntersectsYear(promotion, selectedYear)) continue
      const year = promotionDisplayYear(promotion)
      if (year === null) continue
      counts.set(year, (counts.get(year) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .map(([year, count]) => ({ year, count }))
      .sort((a, b) => a.year - b.year)
  }, [nonYearFilteredPromotions, searchTerm, selectedYear])

  // Organize promotions into rows by customer/retailer combination
  const promotionRows = useMemo(() => {
    const rowMap = new Map<string, PromotionRow>()
    
    filteredPromotions.forEach(promotion => {
      const customer = promotion.customer || 'Unknown'
      const retailer = promotion.retailer || 'Unknown'
      const key = `${customer}|${retailer}`
      
      if (!rowMap.has(key)) {
        rowMap.set(key, {
          customer,
          retailer,
          promotions: []
        })
      }
      
      rowMap.get(key)!.promotions.push(promotion)
    })
    
    // Sort rows by customer, then by retailer
    return Array.from(rowMap.values()).sort((a, b) => {
      if (a.customer !== b.customer) {
        return a.customer.localeCompare(b.customer)
      }
      return a.retailer.localeCompare(b.retailer)
    })
  }, [filteredPromotions])

  // Scroll to the first search match once the debounced term settles.
  useEffect(() => {
    if (!debouncedSearchTerm || !calendarScrollRef.current) return
    const firstPosition = filteredPromotions
      .map(promotion => calculatePromotionPosition(promotion, weeks))
      .find(position => position !== null)
    if (!firstPosition) return
    // 100px per week column; back off two columns so the match isn't flush against the sticky header.
    calendarScrollRef.current.scrollTo({
      left: Math.max(0, firstPosition.startWeekIndex * 100 - 200),
      behavior: "smooth",
    })
  }, [debouncedSearchTerm])

  const any_calendar_still_loading_initial = calendars.some(
    (calendar_row) => promotion_load_by_calendar[calendar_row.id]?.loading_initial,
  )
  const show_timeline_skeleton = calendars.length > 0 && promotionRows.length === 0 && any_calendar_still_loading_initial

  // Show message if no promotions found
  const hasPromotions = promotionRows.length > 0

  function handleEditPromotion(promotion: Promotion) {
    setEditingPromotion(promotion)
    setIsEditModalOpen(true)
  }

  function handleCloseEditModal() {
    setEditingPromotion(null)
    setIsEditModalOpen(false)
  }

  async function handleSavePromotion(updatedPromotion: Promotion) {
    if (onEditPromotion) {
      onEditPromotion(updatedPromotion)
    }
    handleCloseEditModal()
  }

  return (
    <div className={`space-y-4 ${className || ''}`}>

      {/* Filters */}
      <CalendarFilters
        filterOptions={filterOptions}
        selectedCustomers={filters.customers}
        selectedRetailers={filters.retailers}
        selectedPromotionTypes={filters.promotion_types}
        selectedProducts={filters.products}
        searchTerm={searchTerm}
        onCustomerChange={(customers: string[]) => onFiltersChange({ ...filters, customers })}
        onRetailerChange={(retailers: string[]) => onFiltersChange({ ...filters, retailers })}
        onPromotionTypeChange={(promotion_types: string[]) => onFiltersChange({ ...filters, promotion_types })}
        onProductChange={(products: string[]) => onFiltersChange({ ...filters, products })}
        onSearchChange={onSearchChange}
      />

      {/* Planned vs. actual for exactly what the filters show, so a bucket can be checked against its plan */}
      {(has_active_chip_filters(filters) || searchTerm.trim() !== "") && (
        <FilteredSpendSummary promotions={filteredPromotions} year={selectedYear} />
      )}

      {/* Search matches that live in other years — the grid only renders the selected year */}
      {otherYearSearchMatches.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          <span>Also found in other years:</span>
          {otherYearSearchMatches.map(({ year, count }) => (
            <Button
              key={year}
              variant="outline"
              size="sm"
              onClick={() => onYearChange(year)}
            >
              {count} in {year} →
            </Button>
          ))}
        </div>
      )}

      {/* Calendar Grid */}
      {/* isolate keeps the sticky columns' z-[2000] scoped to the calendar so they don't paint over the app sidebar */}
      <div className="relative border border-gray-200 rounded-lg overflow-hidden isolate">
        {/* Year jump buttons appear when the user scrolls to an edge of the timeline, since the
            grid only spans one year and the year switcher above is easy to miss. */}
        {timelineEdges.atLeftEdge && (
          <Button
            variant="secondary"
            size="sm"
            className="absolute left-52 top-14 z-[2100] border border-gray-300 bg-white shadow-md"
            onClick={() => {
              pendingScrollTargetRef.current = 'year_end'
              onYearChange(selectedYear - 1)
            }}
          >
            ← {selectedYear - 1}
          </Button>
        )}
        {timelineEdges.atRightEdge && (
          <Button
            variant="secondary"
            size="sm"
            className="absolute right-3 top-14 z-[2100] border border-gray-300 bg-white shadow-md"
            onClick={() => onYearChange(selectedYear + 1)}
          >
            {selectedYear + 1} →
          </Button>
        )}
        <div className="overflow-x-auto" ref={calendarScrollRef} onScroll={handleTimelineScroll}>
          <div className="min-w-max relative">
            {/* Month Headers */}
            <div className="flex bg-gray-100 border-b border-gray-200 relative">
              <div className="w-48 min-w-48 p-3 font-medium text-gray-700 border-r border-gray-200 sticky left-0 z-20 bg-gray-100">
                Customer / Retailer
              </div>
              {weeks.map((week, index) => {
                const isYearStart = week.startDate.getMonth() === 0 && week.startDate.getDate() <= 7
                const isMonthStart = week.startDate.getDate() <= 7
                const weekYear = getYear(week.startDate)
                const isSelectedYear = weekYear === selectedYear
                
                return (
                  <div 
                    key={`${weekYear}-${week.weekNumber}-${index}`} 
                    className={`text-center p-2 text-xs border-r border-gray-200 relative ${
                      isMonthStart ? 'font-bold text-gray-900' : 'font-medium text-gray-700'
                    } ${isSelectedYear ? '' : 'bg-gray-50 text-gray-400'}`}
                    style={{ width: '100px', minWidth: '100px' }}
                  >
                    {isYearStart ? 
                      `${getMonthLabel(week.startDate)} ${weekYear}` : 
                      (isMonthStart ? 
                        getMonthLabel(week.startDate) : 
                        `W${week.weekNumber}`)
                    }
                    {/* Year marker line */}
                    {isYearStart && (
                      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-red-500 z-10"></div>
                    )}
                  </div>
                )
              })}
            </div>

                        {/* Promotion Rows */}
            {show_timeline_skeleton ? (
              Array.from({ length: 5 }).map((_, skeleton_index) => (
                <SkeletonPromotionTimelineRow key={skeleton_index} />
              ))
            ) : hasPromotions ? (
              promotionRows.map((row, rowIndex) => {
                // Calculate positions and overlaps for all promotions in this row
                const promotionPositions = row.promotions
                  .map(promotion => {
                    const position = calculatePromotionPosition(promotion, weeks)
                    return position ? { promotion, position } : null
                  })
                  .filter(Boolean) as Array<{ promotion: Promotion; position: any }>
                
                // Calculate vertical offsets for overlapping promotions
                const promotionOffsets = calculatePromotionOffsets(promotionPositions)
                
                // Calculate dynamic row height based on maximum stack level
                const maxStackLevel = Math.max(0, ...promotionOffsets.map(p => p.verticalOffset / 54)) // 52px height + 2px spacing
                const dynamicRowHeight = Math.max(70, 5 + (maxStackLevel + 1) * 54 + 5) // 5px top margin + blocks + 5px bottom margin
                
                return (
                  <div 
                    key={`${row.customer}-${row.retailer}`} 
                    className="flex border-b border-gray-100 hover:bg-blue-50 hover:border-blue-200 transition-all duration-200 group"
                  >
                    {/* Row Header */}
                    <div className="w-48 min-w-48 p-3 border-r border-gray-200 bg-gray-50 group-hover:bg-blue-100 transition-colors duration-200 sticky left-0 z-[2000]">
                      {row.customer !== 'Unknown' && (
                        <div className="text-sm font-medium text-gray-900">{row.customer}</div>
                      )}
                      <div className="text-xs text-gray-600">{row.retailer}</div>
                    </div>
                    
                    {/* Timeline Row */}
                    <div className="relative flex-1 group-hover:bg-blue-50 transition-colors duration-200 overflow-hidden" style={{ height: `${dynamicRowHeight}px` }}>
                      {/* Year marker lines for each row */}
                      {weeks.map((week, index) => {
                        const isYearStart = week.startDate.getMonth() === 0 && week.startDate.getDate() <= 7
                        if (!isYearStart) return null
                        
                        return (
                          <div 
                            key={`year-marker-${index}`}
                            className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                            style={{ left: `${index * 100}px` }}
                          ></div>
                        )
                      })}
                      
                      {promotionOffsets.map(({ promotion, position, verticalOffset }) => (
                        <PromotionBlock
                          key={promotion.id || `${promotion.name}-${promotion.customer}`}
                          promotion={promotion}
                          position={position}
                          onEdit={() => handleEditPromotion(promotion)}
                          rowHeight={dynamicRowHeight}
                          rowIndex={rowIndex}
                          verticalOffset={verticalOffset}
                        />
                      ))}
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="flex border-b border-gray-100">
                <div className="w-48 min-w-48 p-3 border-r border-gray-200 bg-gray-50 sticky left-0 z-[2000]">
                  <div className="text-sm font-medium text-gray-900">No promotions</div>
                </div>
                <div className="flex-1 p-8 text-center text-gray-500">
                  {filteredPromotions.length === 0 ? 
                    'No promotions found for the selected filters and year.' : 
                    'No promotions with valid dates found for the selected year.'
                  }
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Edit Modal */}
      <EnhancedPromotionEditModal
        promotion={editingPromotion}
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleSavePromotion}
        onDelete={onDeletePromotion}
      />

    </div>
  )
}

function SkeletonPromotionTimelineRow() {
  return (
    <div className="flex border-b border-gray-100">
      <div className="w-48 min-w-48 p-3 border-r border-gray-200 bg-gray-50 sticky left-0 z-[2000] space-y-1.5">
        <div className="h-3 w-24 rounded bg-gray-200 animate-pulse" />
        <div className="h-2 w-16 rounded bg-gray-100 animate-pulse" />
      </div>
      <div className="relative flex-1 overflow-hidden bg-white" style={{ height: "76px" }}>
        <div className="absolute left-4 top-1/2 -translate-y-1/2 h-9 w-[40%] min-w-[120px] rounded-md bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 animate-pulse" />
      </div>
    </div>
  )
}

// Helper functions
export interface WeekData {
  weekNumber: number
  startDate: Date
  endDate: Date
  year: number
}

// Padding around the selected year, so boundary-spanning promotions render in full: a January
// promo month is bought from late November (UNFI's Jan 2026 buy window opens 11/30/25), and a
// December window can spill a few days into January.
export const PADDING_WEEKS_BEFORE = 6
export const PADDING_WEEKS_AFTER = 2

export function generateWeeksForYear(year: number): WeekData[] {
  const firstWeekStart = addWeeks(startOfWeek(new Date(year, 0, 1), { weekStartsOn: 1 }), -PADDING_WEEKS_BEFORE) // Monday
  const lastWeekStart = addWeeks(startOfWeek(new Date(year, 11, 31), { weekStartsOn: 1 }), PADDING_WEEKS_AFTER)

  const weeks: WeekData[] = []
  for (let weekStart = firstWeekStart; weekStart <= lastWeekStart; weekStart = addWeeks(weekStart, 1)) {
    weeks.push({
      weekNumber: getISOWeek(weekStart),
      startDate: weekStart,
      endDate: endOfWeek(weekStart, { weekStartsOn: 1 }), // Sunday
      year: getYear(weekStart)
    })
  }

  return weeks
}

/** True when the promotion's date range touches the given calendar year. Promotions without a
 * start date are kept (they can't be positioned, but they shouldn't vanish from row grouping). */
export function promotionIntersectsYear(
  promotion: Pick<Promotion, 'start_date' | 'end_date'>,
  year: number
): boolean {
  if (!promotion.start_date) return true
  const startDate = parseISO(promotion.start_date)
  if (isNaN(startDate.getTime())) return false
  const endDate = promotion.end_date ? parseISO(promotion.end_date) : startDate
  const effectiveEnd = isNaN(endDate.getTime()) ? startDate : endDate
  return startDate.getFullYear() <= year && effectiveEnd.getFullYear() >= year
}

/** Whether a timeline scrolled to `scrollLeft` sits at its far left/right edge (within a small
 * tolerance), which is where the previous/next-year jump buttons appear. */
export function timelineEdgeState(scrollLeft: number, clientWidth: number, scrollWidth: number) {
  const EDGE_TOLERANCE_PX = 8
  return {
    atLeftEdge: scrollLeft <= EDGE_TOLERANCE_PX,
    atRightEdge: scrollLeft + clientWidth >= scrollWidth - EDGE_TOLERANCE_PX,
  }
}

/** The year a promotion belongs to for jump-chip grouping. The end date decides: Nancy's January
 * promos start the prior December, and the end date is the year the planner filed them under. */
export function promotionDisplayYear(promotion: Pick<Promotion, 'start_date' | 'end_date'>): number | null {
  for (const date_text of [promotion.end_date, promotion.start_date]) {
    if (!date_text) continue
    const date = parseISO(date_text)
    if (!isNaN(date.getTime())) return date.getFullYear()
  }
  return null
}

function getMonthLabel(date: Date): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return months[date.getMonth()]
}

function calculatePromotionOffsets(promotionPositions: Array<{ promotion: Promotion; position: any }>) {
  const blockHeight = 52 // Height of each promotion block
  const blockSpacing = 2 // Spacing between stacked blocks
  
  // Sort promotions by start date to process them in chronological order
  const sortedPromotions = [...promotionPositions].sort((a, b) => {
    const dateA = parseISO(a.promotion.start_date || '')
    const dateB = parseISO(b.promotion.start_date || '')
    return dateA.getTime() - dateB.getTime()
  })
  
  const offsets: Array<{ promotion: Promotion; position: any; verticalOffset: number }> = []
  const activeStacks: Array<{ 
    startDate: Date; 
    endDate: Date; 
    stackLevel: number;
    endWeekIndex: number; // Keep for efficient cleanup
  }> = []
  
  for (const { promotion, position } of sortedPromotions) {
    if (!promotion.start_date || !promotion.end_date) {
      // If no dates, don't stack
      offsets.push({ promotion, position, verticalOffset: 0 })
      continue
    }
    
    const startDate = parseISO(promotion.start_date)
    const endDate = parseISO(promotion.end_date)
    
    // Validate dates
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      offsets.push({ promotion, position, verticalOffset: 0 })
      continue
    }
    
    // Remove expired stacks (promotions that have ended before this one starts)
    for (let i = activeStacks.length - 1; i >= 0; i--) {
      if (activeStacks[i].endDate < startDate) {
        activeStacks.splice(i, 1)
      }
    }
    
    // Check for overlaps with existing active stacks using exact dates
    // Two promotions overlap if: start1 <= end2 AND start2 <= end1
    const overlappingStacks = activeStacks.filter(stack => 
      startDate <= stack.endDate && endDate >= stack.startDate
    )
    
    let stackLevel = 0
    if (overlappingStacks.length > 0) {
      // Find the next available stack level
      const usedLevels = overlappingStacks.map(stack => stack.stackLevel)
      stackLevel = 0
      while (usedLevels.includes(stackLevel)) {
        stackLevel++
      }
    }
    
    // Add this promotion to active stacks
    activeStacks.push({
      startDate,
      endDate,
      stackLevel,
      endWeekIndex: position.endWeekIndex
    })
    
    // Calculate vertical offset
    const verticalOffset = stackLevel * (blockHeight + blockSpacing)
    
    offsets.push({
      promotion,
      position,
      verticalOffset
    })
  }
  
  return offsets
}

export function calculatePromotionPosition(promotion: Pick<Promotion, 'name' | 'start_date' | 'end_date'>, weeks: WeekData[]) {
  if (!promotion.start_date || !promotion.end_date) return null
  
  // Parse dates using date-fns parseISO as per CLAUDE.md guidelines
  const startDate = parseISO(promotion.start_date)
  const endDate = parseISO(promotion.end_date)
  
  // Validate dates
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    console.warn('Invalid date format for promotion:', promotion.name, promotion.start_date, promotion.end_date)
    return null
  }
  
  const startYear = startDate.getFullYear()
  const endYear = endDate.getFullYear()
  
  // Find which weeks this promotion spans
  let startWeekIndex = -1
  let endWeekIndex = -1
  
  // First pass: find all weeks that contain the start date
  const containingWeeks = []
  for (let i = 0; i < weeks.length; i++) {
    const week = weeks[i]
    if (isWithinInterval(startDate, { start: week.startDate, end: week.endDate })) {
      containingWeeks.push({ index: i, week })
    }
  }
  
  // For start week: prefer weeks that belong to the same year as the promotion start date
  if (containingWeeks.length > 0) {
    // Look for a week that belongs to the same year first
    const sameYearWeek = containingWeeks.find(({ week }) => week.year === startYear)
    if (sameYearWeek) {
      startWeekIndex = sameYearWeek.index
    } else {
      // Fall back to the first containing week
      startWeekIndex = containingWeeks[0].index
    }
  }
  
  // For end week: find the last week that contains the end date
  for (let i = 0; i < weeks.length; i++) {
    const week = weeks[i]
    if (isWithinInterval(endDate, { start: week.startDate, end: week.endDate })) {
      endWeekIndex = i
    }
  }
  
  // If we didn't find an end week, the promotion extends beyond the visible weeks
  if (startWeekIndex !== -1 && endWeekIndex === -1 && endDate > weeks[weeks.length - 1].endDate) {
    endWeekIndex = weeks.length - 1
  }

  // A promotion starting before the grid's first week renders clipped at the left edge (the grid
  // now only spans the selected year plus padding, and some windows open earlier than that).
  if (startWeekIndex === -1 && weeks.length > 0 && startDate < weeks[0].startDate && endDate >= weeks[0].startDate) {
    startWeekIndex = 0
    if (endWeekIndex === -1 && endDate > weeks[weeks.length - 1].endDate) {
      endWeekIndex = weeks.length - 1
    }
  }

  if (startWeekIndex === -1 || endWeekIndex === -1) {
    console.warn('Could not position promotion:', promotion.name, 'start:', startDate, 'end:', endDate)
    return null
  }
  
  return {
    startWeekIndex,
    endWeekIndex,
    spanWeeks: endWeekIndex - startWeekIndex + 1
  }
}
